import { makeStyles } from '@map/theme';
import InputAdornment from '@mui/material/InputAdornment';
import { compareComponents } from '@shared/util/compareComponents';
import Button from '@ui/Button';
import CalendarIcon from '@ui/Icon/library/CalendarIcon';
import CloseIcon from '@ui/Icon/library/CloseIcon';
import Input from '@ui/formElements/Input';
import {
  ChangeEventHandler,
  ComponentProps,
  ComponentType,
  HTMLAttributes,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import TextFormField from '../TextFormField';
import { CommonFieldProps } from '../commonTypes';

const useStyles = makeStyles(({ spacing }) => ({
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2),
  },
  button: {
    '&::before': {
      all: 'unset',
    },
  },
}));

type DateType = 'date' | 'month' | 'year';

const padValue = (value: string, type: DateType) => {
  if (type === 'year' && value.length === 4) {
    return `${value.padStart(4, ' ')}-01-01`;
  }
  if (type === 'month' && value.length === 7) {
    return `${value}-01`;
  }
  return value;
};

const trimValue = (value: string, type: DateType) => {
  if (type === 'year') {
    return value.substr(0, 4).trim();
  }
  if (type === 'month') {
    return value.substr(0, 7);
  }
  return value;
};

type Props = CommonFieldProps & {
  value: string;
  type: DateType;
  minDate?: string;
  maxDate?: string;
  onChange: (value: string) => void;
  margin?: 'none' | 'dense' | 'normal';
  required?: boolean;
  onBlur?: HTMLAttributes<any>['onBlur'];
  TextFieldComponent?: ComponentType<ComponentProps<typeof TextFormField | typeof Input>>;
  isClearable?: boolean;
};

const MobileDateField: FC<Props> = (props) => {
  const {
    type,
    value,
    onChange,
    TextFieldComponent = TextFormField,
    minDate,
    maxDate,
    isClearable,
    ...childProps
  } = props;
  const { cx, classes } = useStyles();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      onChange(padValue(e.target.value, type));
    },
    [onChange, type],
  );

  const endAdornment = useMemo(() => {
    if (type === 'year' || childProps.disabled) {
      return;
    }

    return (
      <InputAdornment position="end" className={classes.buttonContainer}>
        <Button
          size="medium"
          variant="tertiary"
          className={cx(isClearable && classes.button)}
          onClick={() => {
            const input = inputRef.current;
            if (input) {
              // focus works on ios, click works on android. *facepalm*
              input.focus(); // iOS
              input.click(); // Android
            }
          }}
        >
          <CalendarIcon />
        </Button>
        {isClearable && (
          <Button
            size="medium"
            className={classes.button}
            variant="tertiary"
            onClick={() => {
              const input = inputRef.current;
              if (input) {
                input.value = '';
                onChange('');
              }
            }}
            onMouseDown={(e) => {
              // prevent default to prevent input blur that causes revalidation
              e.preventDefault();
            }}
          >
            <CloseIcon />
          </Button>
        )}
      </InputAdornment>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const commonProps = {
    type: type === 'year' ? 'number' : type,
    value: trimValue(value || '', type),
    inputRef,
    onChange: handleChange,
    InputProps: { endAdornment },
    inputProps: { min: minDate, max: maxDate },
    ...childProps,
  };

  return compareComponents(TextFieldComponent, TextFormField) ? (
    <TextFieldComponent {...commonProps} />
  ) : (
    <TextFieldComponent {...commonProps} InputLabelProps={{ shrink: true }} />
  );
};

export default MobileDateField;
